import React, { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';
import cccApi, { RecuperacaoMarcacao, WorkspaceMotivoData } from '../api/cccApi';
import {
  isWorkspaceMotivoIncomplete,
  SEM_INTERESSE_MOTIVO_WARNING_TEXT,
  PENDENTE_MOTIVO_WARNING_TEXT,
  WORKSPACE_MOTIVO_CONFIG,
  workspaceMotivoLabel,
  WorkspaceMotivoVariant,
} from '../config/workspaceMotivos';
import { readWorkspaceMotivoKeys, toggleMotivoKey } from '../utils/motivoKeys';
import { RecuperacaoMotivoWarningBanner } from './RecuperacaoMotivoWarning';

type Props = {
  variant: WorkspaceMotivoVariant;
  marcacaoId: string;
  initial?: WorkspaceMotivoData | null;
  disabled?: boolean;
  onSaved?: (marcacao: RecuperacaoMarcacao) => void;
};

const formatSavedAt = (iso?: string | null) => {
  if (!iso) return null;
  try {
    return new Date(iso.replace('Z', '')).toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const RecuperacaoWorkspaceMotivo: React.FC<Props> = ({
  variant,
  marcacaoId,
  initial,
  disabled,
  onSaved,
}) => {
  const categories = WORKSPACE_MOTIVO_CONFIG[variant].categories;
  const [selectedKeys, setSelectedKeys] = useState<string[]>(() => readWorkspaceMotivoKeys(initial));
  const [feedback, setFeedback] = useState(initial?.feedback || '');
  const [savingMotivo, setSavingMotivo] = useState(false);
  const [savingFeedback, setSavingFeedback] = useState(false);
  const [meta, setMeta] = useState<{
    updated_at?: string | null;
    updated_by_name?: string | null;
  }>({
    updated_at: initial?.updated_at,
    updated_by_name: initial?.updated_by_name,
  });

  useEffect(() => {
    setSelectedKeys(readWorkspaceMotivoKeys(initial));
    setFeedback(initial?.feedback || '');
    setMeta({
      updated_at: initial?.updated_at,
      updated_by_name: initial?.updated_by_name,
    });
  }, [marcacaoId, initial?.motivo_keys, initial?.motivo_key, initial?.feedback, initial?.updated_at, initial?.updated_by_name]);

  const persist = useCallback(
    async (payload: { motivo_keys?: string[]; feedback?: string }) => {
      const res =
        variant === 'sem_interesse'
          ? await cccApi.updateSemInteresseMotivo(marcacaoId, payload)
          : await cccApi.updatePendenteMotivo(marcacaoId, payload);
      if (!res.success || !res.marcacao) {
        message.error(res.message || 'Não foi possível guardar o motivo');
        return null;
      }
      const claim = res.marcacao.recuperacao_claim;
      const saved =
        variant === 'sem_interesse' ? claim?.sem_interesse_motivo : claim?.pendente_motivo;
      if (saved) {
        setSelectedKeys(readWorkspaceMotivoKeys(saved));
        setMeta({
          updated_at: saved.updated_at,
          updated_by_name: saved.updated_by_name,
        });
      }
      onSaved?.(res.marcacao);
      return res.marcacao;
    },
    [marcacaoId, onSaved, variant]
  );

  const handleToggleMotivo = async (key: string) => {
    if (disabled || savingMotivo) return;
    const next = toggleMotivoKey(selectedKeys, key);
    setSavingMotivo(true);
    try {
      const prev = selectedKeys;
      setSelectedKeys(next);
      const ok = await persist({ motivo_keys: next, feedback });
      if (!ok) setSelectedKeys(prev);
      else message.success(next.length ? 'Motivos guardados' : 'Motivos removidos');
    } finally {
      setSavingMotivo(false);
    }
  };

  const handleSaveFeedback = async () => {
    if (disabled || savingFeedback) return;
    setSavingFeedback(true);
    try {
      const ok = await persist({ motivo_keys: selectedKeys, feedback: feedback.trim() });
      if (ok) message.success('Comentário guardado');
    } finally {
      setSavingFeedback(false);
    }
  };

  const savedLabel = formatSavedAt(meta.updated_at);
  const motivoIncomplete = isWorkspaceMotivoIncomplete({
    motivo_keys: selectedKeys,
    feedback: feedback.trim() || null,
  });
  const needsOutroFeedback =
    (variant === 'pendente' && selectedKeys.includes('outro-pendente')) ||
    (variant === 'sem_interesse' && selectedKeys.includes('outro-motivo'));
  const warningMessage =
    variant === 'pendente'
      ? PENDENTE_MOTIVO_WARNING_TEXT
      : variant === 'sem_interesse'
        ? SEM_INTERESSE_MOTIVO_WARNING_TEXT
        : undefined;

  return (
    <div className="rec-motivo">
      {motivoIncomplete && <RecuperacaoMotivoWarningBanner message={warningMessage} />}

      {categories.map((category) => (
        <fieldset key={category.id} className="rec-motivo-category" disabled={disabled || savingMotivo}>
          <legend className="rec-motivo-category-title">{category.label}</legend>
          <div className="rec-motivo-options">
            {category.options.map((opt) => {
              const isSelected = selectedKeys.includes(opt.key);
              return (
                <button
                  key={opt.key}
                  type="button"
                  className={`rec-motivo-option ${isSelected ? 'is-selected' : ''}`}
                  aria-pressed={isSelected}
                  onClick={() => handleToggleMotivo(opt.key)}
                >
                  <span className="rec-motivo-option-check" aria-hidden>
                    {isSelected ? '✓' : ''}
                  </span>
                  <span>{opt.label}</span>
                </button>
              );
            })}
          </div>
        </fieldset>
      ))}

      <div className="rec-motivo-feedback-block">
        <label className="rec-motivo-feedback-label" htmlFor={`motivo-feedback-${variant}-${marcacaoId}`}>
          Comentário adicional
          {needsOutroFeedback && <span className="rec-required-asterisk"> *</span>}
        </label>
        <div className="rec-motivo-feedback-row">
          <textarea
            id={`motivo-feedback-${variant}-${marcacaoId}`}
            className="rec-motivo-feedback-input"
            rows={4}
            placeholder="Detalhe o contexto, próximos passos ou o que está pendente…"
            value={feedback}
            disabled={disabled}
            onChange={(e) => setFeedback(e.target.value)}
          />
          <button
            type="button"
            className="rec-btn rec-btn-primary"
            disabled={disabled || savingFeedback}
            onClick={handleSaveFeedback}
          >
            {savingFeedback ? 'A guardar…' : 'Guardar comentário'}
          </button>
        </div>
      </div>

      {selectedKeys.length > 0 && (
        <p className="rec-motivo-summary">
          Motivos selecionados:{' '}
          <strong>
            {selectedKeys.map((k) => workspaceMotivoLabel(variant, k) || k).join(', ')}
          </strong>
        </p>
      )}

      {savedLabel && (
        <p className="rec-motivo-meta">
          Última atualização{meta.updated_by_name ? ` por ${meta.updated_by_name}` : ''}: {savedLabel}
        </p>
      )}
    </div>
  );
};

export default RecuperacaoWorkspaceMotivo;
